/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { defineMessages } from 'react-intl'

import type { IconType } from '@elastic/eui'

import type { ProjectTypeWithWorkplaceAI } from '@modules/ui-types/projects'
import type {
  ElasticsearchOptimizedFor,
  ObservabilityProjectProductTier,
} from '@modules/project-user-api/v1/types'

import type { MessageDescriptor } from 'react-intl'

const projectTypes = defineMessages<ProjectTypeWithWorkplaceAI>({
  elasticsearch: {
    id: 'projects.pretty.type.elasticsearch',
    defaultMessage: 'Elasticsearch',
  },
  observability: {
    id: 'projects.pretty.type.observability',
    defaultMessage: 'Observability',
  },
  security: {
    id: 'projects.pretty.type.security',
    defaultMessage: 'Security',
  },
  workplaceai: {
    id: 'projects.pretty.type.workplaceai',
    defaultMessage: 'Workplace AI',
  },
})

const elasticsearchProjectSubtypes = defineMessages<ElasticsearchOptimizedFor>({
  general_purpose: {
    id: 'projects.pretty.subtype.elasticsearch-general',
    defaultMessage: 'General',
  },
  vector: {
    id: 'projects.pretty.subtype.elasticsearch-vector',
    defaultMessage: 'Vector',
  },
})

const observabilityProjectSubtypes: Record<ObservabilityProjectProductTier, MessageDescriptor> =
  defineMessages({
    logs_essentials: {
      id: 'projects.pretty.subtype.observability-logs-essentials',
      defaultMessage: 'Logs Essentials',
    },
    complete: {
      id: 'projects.pretty.subtype.observability-complete',
      defaultMessage: 'Complete',
    },
  })

export function getPrettyProjectType(type: ProjectTypeWithWorkplaceAI): MessageDescriptor {
  return projectTypes[type]
}

export function getProjectTypeIcon(type: ProjectTypeWithWorkplaceAI): IconType {
  const projectLogo: Record<ProjectTypeWithWorkplaceAI, IconType> = {
    elasticsearch: 'logoElasticsearch',
    observability: 'logoObservability',
    security: 'logoSecurity',
    workplaceai: 'logoWorkplaceSearch',
  }

  return projectLogo[type]
}

export function getPrettyElasticsearchProjectSubtype(
  type: ElasticsearchOptimizedFor,
): MessageDescriptor {
  return elasticsearchProjectSubtypes[type]
}

export function getPrettyObservabilityProjectSubtype(
  type?: ObservabilityProjectProductTier,
): MessageDescriptor {
  return type ? observabilityProjectSubtypes[type] : observabilityProjectSubtypes.complete
}

export function getDisplayProjectId(projectId: string) {
  return projectId.slice(0, 6)
}
